import React, { useEffect, useRef, useState } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useAuthStore } from '../../store/authStore';
import { loadGoogleIdentity, onGoogleCredential } from '../../services/googleIdentity';

// Google renders its button between 200 and 400 pixels wide.
const MIN_WIDTH = 200;
const MAX_WIDTH = 400;
// Height of Google's large button, held open while the script loads.
const BUTTON_HEIGHT = 44;

interface GoogleSignInButtonProps {
  /** Called once the account is signed in, to move on from the auth page. */
  onSignedIn: () => void;
  /** Blocks the button while another sign-in is in flight. */
  disabled?: boolean;
}

/**
 * Google's own "Continue with Google" button, followed by an "or" divider for
 * the email form beneath it. Signing in goes through the auth store exactly
 * like a password login; a failure shows in the page's error alert.
 *
 * Renders nothing when Google's script cannot be loaded (blocked, offline),
 * so the email form is all the user sees.
 */
const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({ onSignedIn, disabled = false }) => {
  const slotRef = useRef<HTMLDivElement>(null);
  const [unavailable, setUnavailable] = useState(false);
  const googleLogin = useAuthStore((state) => state.googleLogin);

  // The page passes a fresh closure each render; always call the latest one.
  const onSignedInRef = useRef(onSignedIn);
  useEffect(() => {
    onSignedInRef.current = onSignedIn;
  });

  useEffect(
    () =>
      onGoogleCredential(async (credential) => {
        if (useAuthStore.getState().isLoading) return;
        try {
          await googleLogin(credential);
          onSignedInRef.current();
        } catch {
          // Error is surfaced through the store.
        }
      }),
    [googleLogin]
  );

  useEffect(() => {
    let cancelled = false;
    let observer: ResizeObserver | undefined;

    loadGoogleIdentity()
      .then((googleId) => {
        const slot = slotRef.current;
        if (cancelled || !slot) return;

        let renderedWidth = 0;
        const render = () => {
          const width = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.floor(slot.clientWidth)));
          if (width === renderedWidth) return;
          renderedWidth = width;
          slot.textContent = '';
          googleId.renderButton(slot, {
            theme: 'outline',
            size: 'large',
            text: 'continue_with',
            width,
          });
        };

        render();
        // The button has a fixed pixel width, so draw it again when the card
        // gets narrower or wider (a phone rotating, a window resizing).
        if (typeof ResizeObserver !== 'undefined') {
          observer = new ResizeObserver(render);
          observer.observe(slot);
        }
      })
      .catch(() => {
        if (!cancelled) setUnavailable(true);
      });

    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, []);

  if (unavailable) return null;

  return (
    <Box>
      <Box
        ref={slotRef}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          minHeight: BUTTON_HEIGHT,
          ...(disabled && { pointerEvents: 'none', opacity: 0.6 }),
        }}
      />
      <Divider sx={{ my: 3 }}>
        <Typography variant="body2" color="text.secondary">
          or
        </Typography>
      </Divider>
    </Box>
  );
};

export default GoogleSignInButton;
